// OpenRouter API service for AI-powered recipe and meal plan generation

// Get API configuration from environment variables
const OPENROUTER_API_KEY = process.env.REACT_APP_OPENROUTER_API_KEY;
const OPENROUTER_URL = process.env.REACT_APP_OPENROUTER_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL || window.location.origin;
const SITE_NAME = process.env.REACT_APP_SITE_NAME || 'Meal Maestro';

/**
 * Generate a recipe using the OpenRouter API
 * @param {Object} params - Recipe generation parameters
 * @returns {Promise<string>} Generated recipe text
 */
export const generateRecipe = async (params) => {
  try {
    // Create a detailed prompt from the parameters
    const prompt = createRecipePrompt(params);
    
    const response = await fetch(OPENROUTER_URL, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENROUTER_API_KEY}`,
        "HTTP-Referer": SITE_URL,
        "X-Title": SITE_NAME,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        "model": "google/gemma-3-12b-it:free",
        "messages": [
          {
            "role": "system",
            "content": "You are a professional chef and nutritionist who creates detailed, healthy recipes."
          },
          {
            "role": "user",
            "content": prompt
          }
        ]
      })
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error("Error generating recipe:", error);
    throw error;
  }
};

/**
 * Generate a meal plan using the OpenRouter API
 * @param {Object} params - Meal plan parameters
 * @returns {Promise<string>} Generated meal plan text
 */
export const generateMealPlan = async (params) => {
  try {
    // Create a detailed prompt from the parameters
    const prompt = createMealPlanPrompt(params);
    
    const response = await fetch(OPENROUTER_URL, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENROUTER_API_KEY}`,
        "HTTP-Referer": SITE_URL,
        "X-Title": SITE_NAME,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        "model": "meta-llama/llama-3.3-70b-instruct:free",
        "messages": [
          {
            "role": "system",
            "content": "You are a professional nutritionist creating customized meal plans."
          },
          {
            "role": "user",
            "content": prompt
          }
        ]
      })
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    return data.choices[0].message.content;
  } catch (error) {
    console.error("Error generating meal plan:", error);
    throw error;
  }
};

/**
 * Generate grocery list based on ingredients
 * @param {Array} ingredients - List of ingredients 
 */
export async function generateGroceryList(ingredients) {
  const prompt = `Organize the following ingredients into a grocery shopping list grouped by store sections/categories.
  Ingredients: ${ingredients.join(", ")}
  
  Please format your response as valid JSON with category names as keys and arrays of ingredients as values.
  Example format:
  {
    "Produce": ["apples", "lettuce", "carrots"],
    "Dairy": ["milk", "cheese", "yogurt"],
    "Meat": ["chicken", "ground beef"]
  }
  
  Be sure to organize every ingredient into the appropriate category.`;
  
  try {
    const response = await fetch(OPENROUTER_URL, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${OPENROUTER_API_KEY}`,
        "HTTP-Referer": SITE_URL,
        "X-Title": SITE_NAME,
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        "model": "meta-llama/llama-3.3-70b-instruct:free",
        "messages": [
          {
            "role": "system",
            "content": "You are a professional chef and nutritionist helping users create delicious recipes and meal plans. Provide well-structured, clear responses with precise measurements and instructions. Do not use markdown formatting like asterisks (*) or hash symbols (#) in your response."
          },
          {
            "role": "user",
            "content": prompt
          }
        ]
      })
    });
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    const parsedResponse = data.choices[0].message.content;
    
    // Extract JSON from response if needed (in case the model adds extra text)
    try {
      const jsonMatch = parsedResponse.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        return jsonMatch[0];
      }
      return parsedResponse;
    } catch (e) {
      console.error("Error parsing grocery JSON:", e);
      return parsedResponse;
    }
  } catch (error) {
    console.error("Error generating grocery list:", error);
    throw error;
  }
};

// Helper functions for prompt creation
const createRecipePrompt = (params) => {
  return `You are a professional chef creating a detailed recipe using only these available ingredients: ${params.ingredients}.
${params.dietary ? `Dietary restriction to follow: ${params.dietary}` : ''}
Meal type: ${params.mealType || 'dinner'}
${params.cuisine ? `Cuisine preference: ${params.cuisine}` : ''}
${params.cookingTime && params.cookingTime !== 'any' ? `Cooking time preference: ${params.cookingTime}` : ''}
${params.complexity ? `Complexity level: ${params.complexity}` : ''}

Create a well-structured, restaurant-quality recipe with the following sections:

1. Recipe Title (be creative but descriptive)
2. Preparation Time: (specify in minutes)
3. Cooking Time: (specify in minutes)
4. Servings: (specify number)
5. Ingredients: (list with precise measurements)
6. Instructions: (detailed, numbered steps)
7. Nutritional Information: (per serving)
8. Tips for Success: (techniques, substitutions, etc.)

IMPORTANT: Do not use markdown formatting symbols like asterisks (*) or hash symbols (#) in your response. Use plain text only.

Format each section clearly and include all sections. Your recipe should be well-balanced and delicious while using the available ingredients creatively. If there are essential ingredients missing from the list provided, you may suggest alternatives from the available ingredients.`;
};

const createMealPlanPrompt = (params) => {
  return `Create a detailed ${params.days}-day meal plan following these dietary restrictions: ${params.dietary || 'None'}.
Nutritional goals: ${JSON.stringify(params.nutritionalGoals || {})}.

For each day, include:
1. Breakfast
2. Lunch
3. Dinner
4. Snacks
5. Total nutritional summary for the day

IMPORTANT: Do not use markdown formatting symbols like asterisks (*) or hash symbols (#) in your response. Use plain text only.

Format each meal with:
- Name of dish
- Brief description
- Main ingredients
- Approximate nutritional values`;
};
